import React, { useEffect, useState, useMemo } from 'react';
import { Plus, Trash2, Loader2, Calendar, ArrowUpDown, Save, X, Edit3, ArrowLeft, Eye, MapPin, Users, Check, UserPlus, Search } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { useReorder } from '../../lib/useReorder';
import ImageUploader from '../../components/ImageUploader';
import RichTextEditor from '../../components/RichTextEditor';
import RichContentBlocksEditor from '../../components/RichContentBlocksEditor';
import ContentPreviewModal from '../../components/ContentPreviewModal';
import StatusSelector from '../../components/StatusSelector';
import StatusBadge from '../../components/StatusBadge';

const EMPTY = { title: '', description: '', content_blocks: [], event_date: '', event_end_date: '', location: '', registration_url: '', image_url: '', is_past: false, status: 'draft', is_private: false, audience: 'public', allow_registration: false, capacity: null };

export default function AdminEventsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [registrationsFor, setRegistrationsFor] = useState(null);
  const dnd = useReorder(items);

  async function refresh() {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/events');
      setItems(data || []);
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, []);

  const visible = useMemo(() => {
    if (!dnd.reorderMode) return items;
    const byId = Object.fromEntries(items.map((n) => [n.id, n]));
    return dnd.orderDraft.map((id) => byId[id]).filter(Boolean);
  }, [items, dnd.reorderMode, dnd.orderDraft]);

  async function saveOrder() {
    await dnd.saveOrder(async (ids) => {
      await api.put('/admin/events/reorder', { ids });
      await refresh();
    });
  }

  function startCreate() { setEditingId(null); setForm(EMPTY); setShowForm(true); setError(''); }
  function startEdit(ev) {
    setEditingId(ev.id);
    // Convert ISO event_date to datetime-local format
    let evDate = ev.event_date || '';
    if (evDate && evDate.length > 10) evDate = evDate.slice(0, 16);
    let evEnd = ev.event_end_date || '';
    if (evEnd && evEnd.length > 10) evEnd = evEnd.slice(0, 16);
    setForm({
      title: ev.title || '', description: ev.description || '',
      content_blocks: Array.isArray(ev.content_blocks) ? ev.content_blocks : [],
      event_date: evDate, event_end_date: evEnd,
      location: ev.location || '', registration_url: ev.registration_url || '',
      image_url: ev.image_url || '', is_past: !!ev.is_past, is_private: !!ev.is_private,
      audience: ev.audience || 'public',
      allow_registration: !!ev.allow_registration,
      capacity: ev.capacity ?? null,
      status: ev.status || (ev.is_private ? 'private_link' : 'public'),
      preview_token: ev.preview_token || '',
    });
    setShowForm(true); setError('');
  }
  function cancelForm() { setShowForm(false); setEditingId(null); setForm(EMPTY); setError(''); }

  async function save() {
    if (!form.title.trim() || !form.event_date) return;
    setSaving(true); setError('');
    try {
      const payload = { ...form, event_date: new Date(form.event_date).toISOString() };
      if (form.event_end_date) payload.event_end_date = new Date(form.event_end_date).toISOString();
      else payload.event_end_date = null;
      if (form.capacity === '' || form.capacity === null) payload.capacity = null;
      else payload.capacity = Number(form.capacity);
      if (editingId) await api.put(`/events/${editingId}`, payload);
      else await api.post('/events', payload);
      cancelForm(); await refresh();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    setSaving(false);
  }

  async function remove(id) {
    if (!window.confirm('¿Eliminar este evento?')) return;
    try { await api.delete(`/events/${id}`); await refresh(); } catch (err) { alert(formatApiError(err.response?.data?.detail)); }
  }

  if (showForm) {
    return (
      <div data-testid="admin-events-page" className="space-y-6 max-w-4xl">
        <div className="flex items-center gap-3">
          <button onClick={cancelForm} data-testid="admin-events-back-btn" className="p-2 rounded-lg text-charcoal-600 hover:bg-gray-100"><ArrowLeft size={18} /></button>
          <div>
            <h1 className="text-2xl font-bold text-charcoal-800">{editingId ? 'Editar evento' : 'Nuevo evento'}</h1>
            <p className="text-charcoal-500 text-sm">{editingId ? 'Actualiza los datos y guarda los cambios.' : 'Crea un nuevo evento.'}</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Título</label>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-events-title" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Fecha y hora de inicio</label>
              <input type="datetime-local" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} data-testid="admin-events-date" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Fecha y hora de fin <span className="text-charcoal-400 font-normal">(opcional)</span></label>
              <input type="datetime-local" value={form.event_end_date || ''} onChange={(e) => setForm({ ...form, event_end_date: e.target.value })} data-testid="admin-events-end-date" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Lugar</label>
              <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Ej: CDMX, online, etc." data-testid="admin-events-location" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-charcoal-600 mb-1.5">URL externa de registro <span className="text-charcoal-400 font-normal">(opcional, alternativa al registro interno)</span></label>
              <input value={form.registration_url} onChange={(e) => setForm({ ...form, registration_url: e.target.value })} placeholder="https://…" data-testid="admin-events-url" className="w-full rounded-xl border border-gray-200 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Imagen destacada</label>
            <ImageUploader value={form.image_url || ''} onChange={(url) => setForm({ ...form, image_url: url })} testid="admin-events-image" shape="rect" />
          </div>

          <div>
            <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Descripción principal</label>
            <RichTextEditor
              value={form.description}
              onChange={(html) => setForm({ ...form, description: html })}
              placeholder="Describe el evento (puedes usar negritas, listas, links, imágenes…)"
              testid="admin-events-description"
              minHeight={260}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Bloques adicionales (opcionales)</label>
            <RichContentBlocksEditor
              value={form.content_blocks}
              onChange={(blocks) => setForm({ ...form, content_blocks: blocks })}
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4 p-4 rounded-xl border border-cmic-100 bg-cmic-50/30">
            <div>
              <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Audiencia</label>
              <select value={form.audience || 'public'} onChange={(e) => setForm({ ...form, audience: e.target.value })}
                data-testid="admin-events-audience"
                className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500">
                <option value="public">🌍 Público — visible para todos en /eventos</option>
                <option value="members">🔒 Solo miembros — solo en la Zona Interna</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Cupo máximo <span className="text-charcoal-400 font-normal">(vacío = sin límite)</span></label>
              <input type="number" min="0" value={form.capacity ?? ''} onChange={(e) => setForm({ ...form, capacity: e.target.value === '' ? null : Number(e.target.value) })}
                data-testid="admin-events-capacity"
                placeholder="Ej: 50"
                className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500" />
            </div>
            <label className="inline-flex items-start gap-2 text-sm text-charcoal-700 sm:col-span-2">
              <input type="checkbox" checked={!!form.allow_registration} onChange={(e) => setForm({ ...form, allow_registration: e.target.checked })}
                data-testid="admin-events-allow-registration"
                className="mt-0.5 accent-cmic-600" />
              <span>
                <span className="font-medium">Permitir registro interno</span>
                <span className="block text-xs text-charcoal-500">Los miembros logueados verán un botón "Registrarme" en el evento. Tú podrás marcarles asistencia.</span>
              </span>
            </label>
          </div>

          <div className="flex flex-wrap gap-4">
            <label className="inline-flex items-center gap-2 text-sm text-charcoal-700">
              <input type="checkbox" checked={!!form.is_past} onChange={(e) => setForm({ ...form, is_past: e.target.checked })} data-testid="admin-events-past" />
              Evento pasado (mostrar en archivo)
            </label>
          </div>

          <StatusSelector
            value={form.status || 'draft'}
            onChange={(s) => setForm({ ...form, status: s })}
            previewToken={form.preview_token}
            previewPath="/es/eventos/preview"
          />

          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">{error}</div>}

          <div className="flex gap-2 pt-2 border-t border-gray-100">
            <button onClick={save} disabled={saving || !form.title.trim() || !form.event_date} data-testid="admin-events-save-btn" className="inline-flex items-center gap-2 bg-cmic-600 text-white rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-cmic-700 disabled:opacity-50">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}{editingId ? 'Guardar cambios' : (form.status === 'public' ? 'Crear y publicar' : 'Crear evento')}
            </button>
            <button onClick={() => setShowPreview(true)} disabled={!form.title.trim()} data-testid="admin-events-preview-btn" className="inline-flex items-center gap-2 bg-white border border-cmic-200 text-cmic-700 rounded-lg px-5 py-2.5 text-sm font-medium hover:bg-cmic-50 disabled:opacity-50">
              <Eye size={14} /> Vista previa
            </button>
            <button onClick={cancelForm} className="inline-flex items-center gap-2 bg-white border border-gray-200 text-charcoal-700 rounded-lg px-5 py-2.5 text-sm font-medium hover:bg-gray-50">Cancelar</button>
          </div>
        </div>
        <ContentPreviewModal
          open={showPreview}
          onClose={() => setShowPreview(false)}
          title={form.title}
          imageUrl={form.image_url}
          contentHtml={form.description}
          contentBlocks={form.content_blocks}
          previewToken={form.preview_token}
          previewPath="/es/eventos/preview"
          metaLine={
            <span className="inline-flex items-center gap-3 flex-wrap text-charcoal-500">
              {form.event_date && (
                <span className="inline-flex items-center gap-1.5"><Calendar size={14} className="text-cmic-500" />{new Date(form.event_date).toLocaleString('es-MX', { dateStyle: 'long', timeStyle: 'short' })}</span>
              )}
              {form.location && (
                <span className="inline-flex items-center gap-1.5"><MapPin size={14} className="text-cmic-500" />{form.location}</span>
              )}
            </span>
          }
        />
      </div>
    );
  }

  return (
    <div data-testid="admin-events-page" className="space-y-6">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-charcoal-800 flex items-center gap-2"><Calendar size={22} />Eventos</h1>
          <p className="text-charcoal-500 mt-1 text-sm">{items.length} eventos · Crea, edita o reordena.</p>
        </div>
        <div className="flex items-center gap-2">
          {!dnd.reorderMode ? (
            <>
              <button onClick={dnd.enterReorder} disabled={items.length < 2} data-testid="admin-events-reorder-btn" className="inline-flex items-center gap-2 bg-white border border-gray-200 text-charcoal-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50">
                <ArrowUpDown size={14} />Reordenar
              </button>
              <button onClick={startCreate} data-testid="admin-events-create-btn" className="inline-flex items-center gap-2 bg-cmic-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-cmic-700">
                <Plus size={14} />Nuevo evento
              </button>
            </>
          ) : (
            <>
              <button onClick={dnd.cancelReorder} className="inline-flex items-center gap-2 bg-white border border-gray-200 text-charcoal-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50"><X size={14} />Cancelar</button>
              <button onClick={saveOrder} disabled={dnd.saving} data-testid="admin-events-save-order-btn" className="inline-flex items-center gap-2 bg-emerald-500 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-emerald-600 disabled:opacity-60">
                {dnd.saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}Guardar orden
              </button>
            </>
          )}
        </div>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin text-cmic-500" size={24} /></div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
          <Calendar size={36} className="text-charcoal-300 mx-auto mb-2" />
          <p className="text-charcoal-500 text-sm">Aún no hay eventos.</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {visible.map((ev) => (
            <li key={ev.id} draggable={dnd.reorderMode} {...(dnd.reorderMode ? dnd.dragProps(ev.id) : {})} data-testid={`admin-events-item-${ev.id}`} className={`bg-white rounded-2xl border p-4 flex items-start gap-4 ${dnd.reorderMode ? 'cursor-move border-amber-200 bg-amber-50/50' : 'border-gray-200 hover:border-cmic-300 transition-all'}`}>
              {ev.image_url && <img src={ev.image_url} alt="" className="w-20 h-20 object-cover rounded-xl flex-shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  {ev.is_past && <span className="text-xs font-medium text-gray-700 bg-gray-100 px-2 py-0.5 rounded-full">pasado</span>}
                  <StatusBadge status={ev.status || (ev.is_private ? 'private_link' : 'public')} />
                  {ev.audience === 'members' && (
                    <span className="text-[10px] font-semibold text-cmic-700 bg-cmic-50 border border-cmic-200 px-2 py-0.5 rounded-full" data-testid={`admin-events-audience-badge-${ev.id}`}>🔒 INTERNO</span>
                  )}
                  {ev.allow_registration && (
                    <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">REGISTRO ABIERTO</span>
                  )}
                  <span className="text-xs text-charcoal-400">{ev.event_date ? new Date(ev.event_date).toLocaleString('es-MX') : ''}</span>
                </div>
                <p className="font-semibold text-charcoal-800 truncate">{ev.title}</p>
                {ev.location && <p className="text-xs text-charcoal-500">📍 {ev.location}</p>}
              </div>
              {!dnd.reorderMode && (
                <div className="flex gap-1 flex-shrink-0">
                  {ev.allow_registration && (
                    <button onClick={() => setRegistrationsFor(ev)} title="Ver registrados" data-testid={`admin-events-registrations-${ev.id}`} className="p-2 rounded-lg text-cmic-600 hover:bg-cmic-50"><Users size={14} /></button>
                  )}
                  <button onClick={() => startEdit(ev)} title="Editar" data-testid={`admin-events-edit-${ev.id}`} className="p-2 rounded-lg text-charcoal-600 hover:bg-gray-100"><Edit3 size={14} /></button>
                  <button onClick={() => remove(ev.id)} title="Eliminar" data-testid={`admin-events-delete-${ev.id}`} className="p-2 rounded-lg text-rose-500 hover:bg-rose-50"><Trash2 size={14} /></button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {registrationsFor && (
        <RegistrationsModal event={registrationsFor} onClose={() => setRegistrationsFor(null)} />
      )}
    </div>
  );
}

function RegistrationsModal({ event, onClose }) {
  const [regs, setRegs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [addUserId, setAddUserId] = useState('');

  async function load() {
    setLoading(true); setError('');
    try {
      const [r, u] = await Promise.all([
        api.get(`/admin/events/${event.id}/registrations`),
        api.get('/admin/community-users'),
      ]);
      setRegs(r.data || []);
      setAllUsers(u.data || []);
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || 'Error'); }
    setLoading(false);
  }
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [event.id]);

  async function toggleAttendance(reg) {
    try {
      await api.put(`/admin/events/${event.id}/registrations/${reg.user_id}`, { attended: !reg.attended });
      await load();
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || 'Error'); }
  }
  async function removeReg(reg) {
    if (!window.confirm(`¿Quitar el registro de ${reg.user?.full_name || reg.user_id}?`)) return;
    try {
      await api.delete(`/admin/events/${event.id}/registrations/${reg.user_id}`);
      await load();
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || 'Error'); }
  }
  async function manualAdd() {
    if (!addUserId) return;
    try {
      await api.post(`/admin/events/${event.id}/registrations`, { user_id: addUserId, notes: 'Registrado por admin' });
      setAddUserId('');
      await load();
    } catch (e) {
      const raw = e.response?.data?.detail;
      const isCapFull = typeof raw === 'string' && raw.startsWith('capacity_full|');
      if (isCapFull) {
        const msg = raw.split('|', 2)[1] || 'El cupo está lleno.';
        if (window.confirm(`${msg}\n\n¿Confirmas registrar a este usuario y exceder el cupo del evento?`)) {
          try {
            await api.post(`/admin/events/${event.id}/registrations?force=true`, { user_id: addUserId, notes: 'Registrado por admin (excede cupo)' });
            setAddUserId('');
            await load();
            return;
          } catch (e2) {
            setError(formatApiError(e2.response?.data?.detail) || 'Error');
            return;
          }
        }
        return;
      }
      setError(formatApiError(raw) || 'Error');
    }
  }

  const registeredIds = new Set(regs.map((r) => r.user_id));
  const availableUsers = allUsers.filter((u) => !registeredIds.has(u.id));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="event-registrations-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div>
            <h2 className="font-bold text-charcoal-800 text-base flex items-center gap-2"><Users size={18} className="text-cmic-600" /> Registrados</h2>
            <p className="text-[11px] text-charcoal-500 mt-0.5">{event.title} · {regs.length} registrado(s){event.capacity ? ` / ${event.capacity}` : ''}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-2 text-xs">{error}</div>}
          {/* Manual add */}
          <div className="border border-gray-100 rounded-xl p-3 bg-gray-50/40">
            <p className="text-xs font-semibold text-charcoal-700 mb-2 uppercase tracking-wider flex items-center gap-1"><UserPlus size={12} /> Registrar manualmente</p>
            <div className="flex gap-2">
              <select value={addUserId} onChange={(e) => setAddUserId(e.target.value)}
                data-testid="registrations-manual-add-select"
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm">
                <option value="">Selecciona un usuario…</option>
                {availableUsers.map((u) => (
                  <option key={u.id} value={u.id}>{u.full_name || u.username} ({u.email || 'sin correo'})</option>
                ))}
              </select>
              <button onClick={manualAdd} disabled={!addUserId}
                data-testid="registrations-manual-add-btn"
                className="px-4 py-2 bg-cmic-600 text-white text-sm font-medium rounded-lg hover:bg-cmic-700 disabled:opacity-50">
                Agregar
              </button>
            </div>
          </div>
          {/* List */}
          {loading ? (
            <div className="py-10 flex items-center justify-center"><Loader2 className="animate-spin text-cmic-500" size={24} /></div>
          ) : regs.length === 0 ? (
            <p className="text-charcoal-500 text-sm text-center py-6">Aún no hay registrados.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-right px-2 py-2 font-medium text-charcoal-400 text-[10px] w-10">#</th>
                    <th className="text-left px-3 py-2 font-medium text-charcoal-600">Miembro</th>
                    <th className="text-left px-3 py-2 font-medium text-charcoal-600">Registrado</th>
                    <th className="text-center px-3 py-2 font-medium text-charcoal-600">Asistió</th>
                    <th className="text-right px-3 py-2 font-medium text-charcoal-600">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {regs.map((r, idx) => (
                    <tr key={r.id} data-testid={`registration-row-${r.user_id}`} className="hover:bg-gray-50/50">
                      <td className="px-2 py-2 text-right text-charcoal-400 text-xs tabular-nums">{idx + 1}</td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-charcoal-800 flex items-center gap-1.5">
                          {r.user?.full_name || r.user?.username || r.user_id}
                          {r.over_capacity && (
                            <span data-testid={`registration-overcap-${r.user_id}`}
                              title="Este usuario fue registrado excediendo el cupo del evento"
                              className="inline-flex items-center text-[9px] font-bold uppercase bg-amber-100 text-amber-800 border border-amber-300 px-1.5 py-0.5 rounded">
                              Excede cupo
                            </span>
                          )}
                        </div>
                        <div className="text-[10px] text-charcoal-500">{r.user?.email || ''}</div>
                      </td>
                      <td className="px-3 py-2 text-[11px] text-charcoal-500">{new Date(r.registered_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                      <td className="px-3 py-2 text-center">
                        <button onClick={() => toggleAttendance(r)}
                          data-testid={`registration-attend-${r.user_id}`}
                          className={`w-7 h-7 rounded-md inline-flex items-center justify-center transition-colors ${r.attended ? 'bg-emerald-500 text-white hover:bg-emerald-600' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}
                          title={r.attended ? 'Asistió — clic para revertir' : 'No asistió — clic para marcar'}>
                          <Check size={14} />
                        </button>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => removeReg(r)}
                          data-testid={`registration-remove-${r.user_id}`}
                          className="text-xs text-rose-600 hover:bg-rose-50 rounded px-2 py-1"><Trash2 size={12} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="border-t border-gray-200 px-5 py-3 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm text-charcoal-600 hover:bg-gray-100 rounded-lg">Cerrar</button>
        </div>
      </div>
    </div>
  );
}
